import { GetRiskAnalysisByCancerTypeResponse } from '@/repositories/risk.repository';
import { formatRiskRoundTwoDecimals } from '@/utils';
import { useBreakpointValue } from '@chakra-ui/react';
import { memo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell } from 'recharts';

type RiskAnalysisChartProps = {
  currentRiskColor: string;
  data: GetRiskAnalysisByCancerTypeResponse['risks'];
  variant?: 'light' | 'dark';
};

export const RiskAnalysisChart = memo(function (props: RiskAnalysisChartProps) {
  const screenSize = useBreakpointValue({ base: 'mobile', md: 'tablet', lg: 'desktop' }, { ssr: false }) || 'mobile';
  const chartData = _getRechartsData(props.data);

  const textColor = props.variant === 'light' ? 'var(--chakra-colors-text-primary)' : 'var(--chakra-colors-tan)';

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart
        data={chartData}
        margin={{
          top: 30,
          right: 0,
          left: -40,
          bottom: 0,
        }}
      >
        <XAxis
          dataKey="rangeLabel"
          axisLine={{ stroke: 'var(--chakra-colors-medBlue)' }}
          tickLine={false}
          tick={{
            fill: textColor,
            fontFamily: 'Inter',
            fontSize: screenSize === 'mobile' ? '12px' : '14px',
            fontWeight: 400,
          }}
          angle={screenSize === 'mobile' ? -35 : 0}
          textAnchor={screenSize === 'mobile' ? 'end' : 'middle'}
        />
        <YAxis
          axisLine={false}
          tickLine={{
            stroke: 'var(--chakra-colors-medBlue)',
            strokeWidth: 1,
          }}
          tickSize={15}
          tickFormatter={(value: number) => ''}
        />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          contentStyle={{
            backgroundColor: 'var(--chakra-colors-gray-800)',
            borderColor: 'var(--chakra-colors-gray-700)',
            borderRadius: '4px',
            padding: '8px 12px',
          }}
          labelStyle={{ display: 'none' }}
          itemStyle={{ color: 'var(--chakra-colors-white)', fontSize: '12px' }}
          formatter={(value: number, name: string, entry: any) => {
            const riskLabel = entry.payload.rangeLabel;
            return [formatRiskRoundTwoDecimals(value), `${name} (${riskLabel})`];
          }}
        />
        <Legend
          align="left"
          wrapperStyle={{
            paddingTop: '20px',
            paddingLeft: '45px',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 400,
          }}
          iconSize={15}
          iconType="circle"
          formatter={(value, entry) => <span style={{ color: textColor, verticalAlign: 'middle' }}>{value}</span>}
        />
        <Bar name="Current Risk" dataKey="current" barSize={10} radius={[4, 4, 4, 4]} fill={props.currentRiskColor}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-current-${index}`} />
          ))}
        </Bar>
        <Bar
          name="Potential Risk"
          dataKey="potential"
          barSize={10}
          radius={[4, 4, 4, 4]}
          fill={props.variant === 'light' ? '#183344FF' : '#38749D99'}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-potential-${index}`} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
});

function _getRechartsData(data: GetRiskAnalysisByCancerTypeResponse['risks']) {
  return data.map((risk) => ({
    rangeLabel: risk.rangeLabel,
    current: risk.ageRelevant ? risk.riskItem.current : null,
    potential: risk.ageRelevant ? risk.riskItem.optimal : null,
    isAgeRelevant: risk.ageRelevant,
  }));
}
